/**
 * Fit a media item's DISPLAY box from its native pixel size, so a small image
 * is never scaled UP to fill the bubble (which reads as pixelated/blurry) —
 * `object-contain` alone would upscale. The box:
 *   ① starts at the native size (the crisp, 1:1 target);
 *   ② downscales to fit the fill limits (`fillWidth` × `maxHeight`) if the
 *      image is larger — big photos still fill the column as before;
 *   ③ floors the LONG side at `minDisplay`, so a tiny image (e.g. 32 px) stays
 *      visible/tappable rather than a few-pixel dot. This floor is the ONLY
 *      case that upscales, and only for genuinely tiny media.
 * Aspect ratio is always preserved. Returns {0,0} for invalid input so the
 * caller can fall back (e.g. keep the fill box until the natural size is known).
 */
export function fitMediaBox(
  naturalWidth: number,
  naturalHeight: number,
  opts: { fillWidth: number; maxHeight: number; minDisplay: number },
): { width: number; height: number } {
  const { fillWidth, maxHeight, minDisplay } = opts;
  let w = naturalWidth;
  let h = naturalHeight;
  if (!(w > 0) || !(h > 0)) {
    return { width: 0, height: 0 };
  }
  // ② downscale-only to the fill limits (never > 1, so native is never enlarged here)
  const down = Math.min(fillWidth / w, maxHeight / h, 1);
  w *= down;
  h *= down;
  // ③ floor the long side (the only upscaling path, for tiny media)
  const up = Math.max(minDisplay / Math.max(w, h), 1);
  w *= up;
  h *= up;
  return { width: Math.round(w), height: Math.round(h) };
}

/**
 * Width of the PRE-LOAD box — used when only the media's ASPECT is known, not
 * its pixel size (a video: dimensions aren't embedded at send, so the exact
 * size only arrives when its thumbnail image loads).
 *
 * Why not just `fillWidth`: the box is styled `width` + `aspect-ratio` +
 * `max-height`. With an explicit width, `max-height` clamps the HEIGHT but
 * cannot shrink the width — so a portrait video (aspect ≈ 0.56) rendered a
 * 520×480 LANDSCAPE box, the blurred placeholder (`object-cover`) filled it
 * edge-to-edge, and the bubble then snapped to ~270 wide once the thumbnail
 * loaded. Deriving the width from the height limit keeps the box aspect-true
 * from the first paint, so there's no reflow:
 *   portrait 0.5625 → min(520, 270) = 270   landscape 1.78 → min(520, 854) = 520
 *   square   1.0    → min(520, 480) = 480   (520 would have overflowed maxHeight)
 */
export function fillBoxWidth(
  aspect: number,
  opts: { fillWidth: number; maxHeight: number },
): number {
  const { fillWidth, maxHeight } = opts;
  if (!(aspect > 0) || !Number.isFinite(aspect)) {
    return fillWidth;
  }
  return Math.round(Math.min(fillWidth, maxHeight * aspect));
}
